import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AawtaLogo } from '../components/AawtaLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState('checking'); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    api.get('/auth/setup-password/validate', { params: { token } })
      .then((r) => {
        if (r.data.valid) { setInfo(r.data); setState('valid'); }
        else setState('invalid');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const score = (p) => [p.length >= 12, /[a-z]/.test(p), /[A-Z]/.test(p), /\d/.test(p), /[^a-zA-Z0-9]/.test(p)].filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error('Passwords do not match');
    if (score(pw) < 5) return toast.error('Password too weak — 12+ chars with upper, lower, number, and special character');
    setSaving(true);
    try {
      await api.post('/auth/setup-password', { token, password: pw });
      setState('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex justify-center mb-6"><AawtaLogo size={44} /></div>

        {state === 'checking' && (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-8">
            <Loader2 className="w-5 h-5 animate-spin" /> Verifying your link…
          </div>
        )}

        {state === 'invalid' && (
          <div className="text-center py-4" data-testid="setup-invalid">
            <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
            <h2 className="font-heading text-xl font-bold text-navy mb-2">Link Invalid or Expired</h2>
            <p className="text-sm text-slate-500 mb-6">
              This password setup link is no longer valid. Please contact an administrator at{' '}
              <a href="mailto:contact@aawta.org" className="text-magenta hover:underline">contact@aawta.org</a> for a new one.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline" data-testid="setup-back-login">Back to Sign In</Button>
          </div>
        )}

        {state === 'valid' && (
          <>
            <h2 className="font-heading text-2xl font-bold text-navy mb-1 text-center">Set Your Password</h2>
            <p className="text-sm text-slate-500 text-center mb-6">
              Welcome{info?.full_name ? `, ${info.full_name}` : ''}! Create a password for <strong>{info?.email}</strong>.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="setup-password-form">
              <div>
                <Label htmlFor="pw">New Password</Label>
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="setup-password" className="mt-1.5" />
                <div className="flex gap-1 mt-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className={`h-1 flex-1 rounded ${i < score(pw) ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">12+ characters with uppercase, lowercase, number, and special character.</p>
              </div>
              <div>
                <Label htmlFor="pw2">Confirm Password</Label>
                <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="setup-password-confirm" className="mt-1.5" />
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-magenta hover:bg-magenta/90 text-white h-11 font-semibold" data-testid="setup-password-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Set Password
              </Button>
            </form>
          </>
        )}

        {state === 'done' && (
          <div className="text-center py-4" data-testid="setup-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-heading text-xl font-bold text-navy mb-2">Password Set!</h2>
            <p className="text-sm text-slate-500 mb-6">Your account is ready. Sign in with your email and new password.</p>
            <Button onClick={() => navigate('/login')} className="bg-magenta hover:bg-magenta/90 text-white" data-testid="setup-go-login">Go to Sign In</Button>
          </div>
        )}
      </div>
    </div>
  );
}
